import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import CalendarView from '../components/CalendarView';
import OrganizationFilter from '../components/OrganizationFilter';
import CategoryTagFilters from '../components/calendar/CategoryTagFilters';
import EventListView from '../components/EventListView';
import EventMapView from '../components/EventMapView';
import config from '../config';
import { themes } from '../config/themes';
import featureFlags from '../config/featureFlags';
import { generateEventSlug, getLocalDateKey, normalizeEventTags, parseEventSlug } from '../utils/eventUtils';
import { categorizeEvent } from '../config/categories';
import { useTheme } from '../context/ThemeContext';
import useDataVersionPolling from '../hooks/useDataVersionPolling';

function EmbeddedHome() {
    const [searchParams] = useSearchParams();
    const themeParam = searchParams.get('theme');

    // ?theme= URL param overrides stored preference
    const { theme: contextTheme } = useTheme();
    const theme = themeParam ? (themes[themeParam] || contextTheme) : contextTheme;

    const now = new Date();
    const thisMonth = now.getMonth() + 1;
    const thisYear = now.getFullYear();

    const [curMonth, setCurMonth] = useState(thisMonth);
    const [curYear, setCurYear] = useState(thisYear);
    const [events, setEvents] = useState([]);
    const [combinedEvents, setCombinedEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    const [toast, setToast] = useState(null);
    const toastTimeout = useRef(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEventSlug, setSelectedEventSlug] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTags, setSelectedTags] = useState(() => new Set());
    const [showFilters, setShowFilters] = useState(false);
    const [showStarredOnly, setShowStarredOnly] = useState(false);
    const [hidePrivateEvents, setHidePrivateEvents] = useState(
        () => localStorage.getItem('hidePrivateEvents') === 'true'
    );
    const [viewMode, setViewMode] = useState(() => {
        if (featureFlags.calendar.enabled) return 'month';
        if (featureFlags.list.enabled) return 'list';
        if (featureFlags.map.enabled) return 'map';
        return 'month';
    });
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isRightPaneOpen, setIsRightPaneOpen] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(() => {
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        return start;
    });
    const processedSlugRef = useRef(null);

    // Helper: fetch events for a month with caching
    const fetchEventsForMonth = async (year, month) => {
        const cacheKey = `events-${year}-${month}`;
        const versionKey = 'events-api-version';
        const cachedData = localStorage.getItem(cacheKey);
        const cachedVersion = localStorage.getItem(versionKey);

        try {
            const versionResponse = await fetch(`${config.apiBaseUrl}${config.endpoints.events.version}`);
            const currentVersion = await versionResponse.text();

            if (cachedVersion !== currentVersion) {
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('events-')) localStorage.removeItem(key);
                });
                localStorage.setItem(versionKey, currentVersion);
            } else if (cachedData) {
                return JSON.parse(cachedData);
            }

            const response = await fetch(`${config.apiBaseUrl}${config.endpoints.events.monthly}/${year}/${month}`);
            const data = await response.json();
            localStorage.setItem(cacheKey, JSON.stringify(data));
            return data;
        } catch (error) {
            return cachedData ? JSON.parse(cachedData) : [];
        }
    };

    // Fetch current month's events
    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const data = await fetchEventsForMonth(curYear, curMonth);
                setEvents(data);
            } catch {
                setEvents([]);
            } finally {
                setIsLoading(false);
                setInitialLoadComplete(true);
            }
        };
        load();
    }, [curMonth, curYear, refreshTick]);

    // Fetch combined events for cross-month weeks
    useEffect(() => {
        const load = async () => {
            if (viewMode !== 'week' || !currentWeek) {
                setCombinedEvents([]);
                return;
            }
            const endOfWeek = new Date(currentWeek);
            endOfWeek.setDate(currentWeek.getDate() + 6);
            const startMonth = currentWeek.getMonth() + 1;
            const startYear = currentWeek.getFullYear();
            const endMonth = endOfWeek.getMonth() + 1;
            const endYear = endOfWeek.getFullYear();

            if (startMonth === endMonth && startYear === endYear) {
                setCombinedEvents([]);
                return;
            }

            setIsLoading(true);
            try {
                const [start, end] = await Promise.all([
                    fetchEventsForMonth(startYear, startMonth),
                    fetchEventsForMonth(endYear, endMonth),
                ]);
                const weekStart = getLocalDateKey(currentWeek);
                const weekEnd = getLocalDateKey(endOfWeek);
                const combined = [...start, ...end].filter(e => {
                    const d = getLocalDateKey(e.startDate);
                    return d >= weekStart && d <= weekEnd;
                });
                setCombinedEvents(combined);
            } catch {
                setCombinedEvents([]);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [viewMode, currentWeek, curMonth, curYear, refreshTick]);

    // Show toast for 3 seconds
    const showToast = (message) => {
        setToast(message);
        if (toastTimeout.current) clearTimeout(toastTimeout.current);
        toastTimeout.current = setTimeout(() => setToast(null), 3000);
    };

    // Poll the events dataversion endpoint; when it changes, bump refreshTick so the
    // fetch effects above re-run and refetch fresh data.
    useDataVersionPolling(() => {
        setRefreshTick(tick => tick + 1);
        showToast('Calendar updated with new events');
    });

    // Extract organizations
    useEffect(() => {
        if (events?.length > 0) {
            setOrganizations([...new Set(events.map(e => e.organization))].sort());
        } else {
            setOrganizations([]);
        }
    }, [events]);

    // Hash navigation
    useEffect(() => {
        const handleHash = () => {
            const hash = window.location.hash.slice(1);
            if (hash) {
                const dateInfo = parseEventSlug(hash);
                if (dateInfo) {
                    if (dateInfo.month !== curMonth || dateInfo.year !== curYear) {
                        setCurMonth(dateInfo.month);
                        setCurYear(dateInfo.year);
                    }
                    setSelectedEventSlug(hash);
                    setIsModalOpen(true);
                }
            } else {
                setSelectedEventSlug(null);
                setIsModalOpen(false);
            }
        };
        handleHash();
        window.addEventListener('hashchange', handleHash);
        return () => window.removeEventListener('hashchange', handleHash);
    }, [curMonth, curYear]);

    // Pre-category/tag event set — what the category/tag legend derives its counts from.
    const legendEvents = (() => {
        let base = viewMode === 'week' && combinedEvents.length > 0 ? combinedEvents : events;
        if (showStarredOnly) {
            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
            base = base.filter(e => starred.includes(generateEventSlug(e)));
        }
        if (hidePrivateEvents) {
            base = base.filter(e => e.eventURL !== '/private-events');
        }
        return selectedOrg === 'all' ? base : base.filter(e => e.organization === selectedOrg);
    })();

    const filteredEvents = (() => {
        let base = selectedCategory
            ? legendEvents.filter(e => categorizeEvent(e) === selectedCategory)
            : legendEvents;
        if (selectedTags.size > 0) {
            base = base.filter(e => {
                const eventTagKeys = normalizeEventTags(e.tags).map(t => t.toLowerCase());
                return [...selectedTags].every(tagKey => eventTagKeys.includes(tagKey));
            });
        }
        return base;
    })();

    const handleCategoryClick = (cat) => {
        setSelectedCategory(prev => (prev === cat ? null : cat));
        setSelectedTags(new Set());
    };

    const handleTagClick = (tagKey) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(tagKey)) next.delete(tagKey);
            else next.add(tagKey);
            return next;
        });
    };

    const gotoToday = () => {
        setCurMonth(thisMonth);
        setCurYear(thisYear);
        const today = new Date();
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        setCurrentWeek(start);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        if (window.innerWidth < 768) setIsRightPaneOpen(false);
    };

    const handleIncrement = () => {
        if (viewMode === 'week') {
            const nw = new Date(currentWeek);
            nw.setDate(nw.getDate() + 7);
            setCurrentWeek(nw);
            const end = new Date(nw);
            end.setDate(nw.getDate() + 6);
            const nm = nw.getMonth() + 1, ny = nw.getFullYear();
            const em = end.getMonth() + 1, ey = end.getFullYear();
            const useEnd = (7 - nw.getDay()) < (end.getDay() + 1);
            if (useEnd) { if (em !== curMonth || ey !== curYear) { setCurMonth(em); setCurYear(ey); } }
            else { if (nm !== curMonth || ny !== curYear) { setCurMonth(nm); setCurYear(ny); } }
        } else {
            if (curMonth === 12) { setCurMonth(1); setCurYear(y => y + 1); }
            else setCurMonth(m => m + 1);
        }
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        if (window.innerWidth < 768) setIsRightPaneOpen(false);
    };

    const handleDecrement = () => {
        if (viewMode === 'week') {
            const nw = new Date(currentWeek);
            nw.setDate(nw.getDate() - 7);
            setCurrentWeek(nw);
            const end = new Date(nw);
            end.setDate(nw.getDate() + 6);
            const nm = nw.getMonth() + 1, ny = nw.getFullYear();
            const em = end.getMonth() + 1, ey = end.getFullYear();
            const useEnd = (7 - nw.getDay()) < (end.getDay() + 1);
            if (useEnd) { if (em !== curMonth || ey !== curYear) { setCurMonth(em); setCurYear(ey); } }
            else { if (nm !== curMonth || ny !== curYear) { setCurMonth(nm); setCurYear(ny); } }
        } else {
            if (curMonth === 1) { setCurMonth(12); setCurYear(y => y - 1); }
            else setCurMonth(m => m - 1);
        }
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        if (window.innerWidth < 768) setIsRightPaneOpen(false);
    };

    const isSwipeFromDayPane = (eventData) => {
        const target = eventData?.event?.target;
        return !!(target?.closest?.('[data-day-swipe-zone="true"]'));
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: (ed) => {
            if (viewMode === 'week' || isSwipeFromDayPane(ed)) return;
            if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) handleIncrement();
        },
        onSwipedRight: (ed) => {
            if (viewMode === 'week' || isSwipeFromDayPane(ed)) return;
            if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) handleDecrement();
        },
        preventDefaultTouchmoveEvent: true,
        trackMouse: false,
    });

    const renderMonthName = () => {
        return new Date(curYear, curMonth - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const renderWeekRange = () => {
        const end = new Date(currentWeek);
        end.setDate(currentWeek.getDate() + 6);
        const sm = currentWeek.toLocaleString('default', { month: 'short' });
        const em = end.toLocaleString('default', { month: 'short' });
        const sd = currentWeek.getDate(), ed = end.getDate(), yr = currentWeek.getFullYear();
        return sm === em ? `${sm} ${sd}–${ed}, ${yr}` : `${sm} ${sd} – ${em} ${ed}, ${yr}`;
    };

    const setMonthView = () => { if (featureFlags.calendar.enabled) { setViewMode('month'); if (window.innerWidth < 768) setIsRightPaneOpen(false); } };
    const setWeekView = () => {
        if (featureFlags.calendar.enabled) {
            setViewMode('week');
            const today = new Date();
            const s = new Date(today);
            s.setDate(today.getDate() - today.getDay());
            setCurrentWeek(s);
            if (window.innerWidth < 768) setIsRightPaneOpen(false);
        }
    };
    const setListView = () => { if (featureFlags.list.enabled) { setViewMode('list'); if (window.innerWidth < 768) setIsRightPaneOpen(false); } };
    const setMapView = () => { if (featureFlags.map.enabled) { setViewMode('map'); if (window.innerWidth < 768) setIsRightPaneOpen(false); } };

    const handleMonthChange = (month, opts = {}) => {
        setCurMonth(month);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        if (window.innerWidth < 768 && !opts.preserveRightPane) setIsRightPaneOpen(false);
    };
    const handleYearChange = (year, opts = {}) => {
        setCurYear(year);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        if (window.innerWidth < 768 && !opts.preserveRightPane) setIsRightPaneOpen(false);
    };

    return (
        // h-full, not app-shell-locked: this renders inside App.js's already
        // correctly-sized locked shell — re-claiming the viewport height here
        // would overflow it (see Home.js for the same fix).
        <div className={`h-full ${theme.background} ${theme.text} flex flex-col`}>
            {/* Header */}
            <div className={`flex-none px-3 py-2 border-b ${theme.border}`}>
                <div className="flex flex-col items-center gap-2">
                    {/* View mode + filter buttons */}
                    <div className="flex items-center gap-1.5 flex-wrap justify-center">
                        {featureFlags.calendar.enabled && (
                            <>
                                <button
                                    onClick={setMonthView}
                                    className={`py-1.5 px-2.5 rounded-lg transition flex flex-col items-center gap-0.5 ${viewMode === 'month' ? theme.viewButtonActive : theme.viewButtonInactive}`}
                                    aria-label="Month view"
                                    aria-pressed={viewMode === 'month'}
                                >
                                    <i className="fas fa-calendar-alt text-xs" aria-hidden="true"></i>
                                    <span className="text-xs leading-none">Month</span>
                                </button>
                                {featureFlags.weekViewButton.enabled && (
                                    <button
                                        onClick={setWeekView}
                                        className={`py-1.5 px-2.5 rounded-lg transition flex flex-col items-center gap-0.5 ${viewMode === 'week' ? theme.viewButtonActive : theme.viewButtonInactive}`}
                                        aria-label="Week view"
                                        aria-pressed={viewMode === 'week'}
                                    >
                                        <i className="fas fa-calendar-week text-xs" aria-hidden="true"></i>
                                        <span className="text-xs leading-none">Week</span>
                                    </button>
                                )}
                            </>
                        )}
                        {featureFlags.list.enabled && (
                            <button
                                onClick={setListView}
                                className={`py-1.5 px-2.5 rounded-lg transition flex flex-col items-center gap-0.5 ${viewMode === 'list' ? theme.viewButtonActive : theme.viewButtonInactive}`}
                                aria-label="List view"
                                aria-pressed={viewMode === 'list'}
                            >
                                <i className="fas fa-list text-xs" aria-hidden="true"></i>
                                <span className="text-xs leading-none">List</span>
                            </button>
                        )}
                        {featureFlags.map.enabled && (
                            <button
                                onClick={setMapView}
                                className={`py-1.5 px-2.5 rounded-lg transition flex flex-col items-center gap-0.5 ${viewMode === 'map' ? theme.viewButtonActive : theme.viewButtonInactive}`}
                                aria-label="Map view"
                                aria-pressed={viewMode === 'map'}
                            >
                                <i className="fas fa-map-marker-alt text-xs" aria-hidden="true"></i>
                                <span className="text-xs leading-none">Map</span>
                            </button>
                        )}
                        <button
                            onClick={() => setShowFilters(f => !f)}
                            className={`py-1.5 px-2.5 rounded-lg transition flex flex-col items-center gap-0.5 ${
                                selectedOrg !== 'all' || hidePrivateEvents || !!selectedCategory || selectedTags.size > 0
                                    ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                                    : showFilters ? theme.viewButtonActive : theme.viewButtonInactive
                            }`}
                            aria-pressed={showFilters}
                            aria-label="Toggle filters"
                        >
                            <i className="fas fa-filter text-xs" aria-hidden="true"></i>
                            <span className="text-xs leading-none">Filter</span>
                        </button>
                    </div>

                    {/* Navigation row */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleDecrement}
                            className={`flex items-center justify-center ${theme.navArrowHover}`}
                            style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${theme.tokens.colors.gridLine}`, color: theme.tokens.colors.muted }}
                            aria-label="Previous"
                        >
                            <i className="fas fa-chevron-left text-sm"></i>
                        </button>
                        <span className={theme.text} style={{ fontFamily: theme.tokens.fonts.display, fontSize: 19, fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {viewMode === 'week' ? renderWeekRange() : renderMonthName()}
                        </span>
                        <button
                            onClick={gotoToday}
                            className={`text-xs px-2 py-1 rounded-lg ${theme.viewButtonInactive}`}
                            style={{ border: `1px solid ${theme.tokens.colors.gridLine}` }}
                            aria-label="Go to today"
                        >
                            Today
                        </button>
                        <button
                            onClick={handleIncrement}
                            className={`flex items-center justify-center ${theme.navArrowHover}`}
                            style={{ width: 30, height: 30, borderRadius: 9, border: `1px solid ${theme.tokens.colors.gridLine}`, color: theme.tokens.colors.muted }}
                            aria-label="Next"
                        >
                            <i className="fas fa-chevron-right text-sm"></i>
                        </button>
                    </div>
                </div>

                {/* Filters panel */}
                {showFilters && (
                    <div className={`rounded-lg shadow-md ${theme.gridBg} p-3 mt-2`}>
                        <OrganizationFilter
                            organizations={organizations}
                            selectedOrg={selectedOrg}
                            onOrgChange={setSelectedOrg}
                            theme={theme}
                            showStarredOnly={showStarredOnly}
                            onStarredChange={setShowStarredOnly}
                            hidePrivateEvents={hidePrivateEvents}
                            onHidePrivateChange={(val) => {
                                localStorage.setItem('hidePrivateEvents', String(val));
                                setHidePrivateEvents(val);
                            }}
                        />
                        {/* Category + tags — shown on every screen size so the Filter panel is
                            always a reliable place to find them, alongside the calendar legend footer. */}
                        <div className="flex items-center gap-2 flex-wrap mt-3 pt-3" style={{ borderTop: `1px solid ${theme.tokens.colors.gridLine}` }}>
                            <CategoryTagFilters
                                tokens={theme.tokens}
                                events={legendEvents}
                                selectedCategory={selectedCategory}
                                onCategoryClick={handleCategoryClick}
                                selectedTags={selectedTags}
                                onTagClick={handleTagClick}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Loading overlay */}
            {isLoading && !initialLoadComplete && (
                <div className={`fixed inset-0 ${theme.modalOverlay} flex items-center justify-center z-50`}>
                    <div className={`${theme.loadingBg} ${theme.loadingText} p-4 rounded-lg`}>
                        <i className="fas fa-spinner fa-spin fa-3x fa-fw"></i>
                        <span className="sr-only">Loading events...</span>
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 overflow-hidden min-h-0">
                {viewMode === 'month' || viewMode === 'week' ? (
                    <div {...swipeHandlers} className="h-full">
                        <CalendarView
                            curMonth={curMonth}
                            curYear={curYear}
                            events={filteredEvents}
                            onModalStateChange={setIsModalOpen}
                            selectedEventSlug={selectedEventSlug}
                            onModalClose={() => {}}
                            isModalOpen={isModalOpen}
                            theme={theme}
                            onMonthChange={handleMonthChange}
                            onYearChange={handleYearChange}
                            viewMode={viewMode}
                            onViewModeChange={setViewMode}
                            currentWeek={currentWeek}
                            selectedEvent={selectedEvent}
                            onEventSelect={setSelectedEvent}
                            onRightPaneChange={setIsRightPaneOpen}
                            isRightPaneOpen={isRightPaneOpen}
                            combinedEvents={combinedEvents}
                            clearSelectedEventSlug={() => {
                                setSelectedEventSlug(null);
                                processedSlugRef.current = null;
                                window.history.pushState({}, '', window.location.pathname);
                            }}
                            legendEvents={legendEvents}
                            selectedCategory={selectedCategory}
                            selectedTags={selectedTags}
                            onCategoryClick={handleCategoryClick}
                            onTagClick={handleTagClick}
                        />
                    </div>
                ) : viewMode === 'list' ? (
                    <EventListView
                        events={filteredEvents}
                        curMonth={curMonth}
                        curYear={curYear}
                        theme={theme}
                        legendEvents={legendEvents}
                        selectedCategory={selectedCategory}
                        selectedTags={selectedTags}
                        onCategoryClick={handleCategoryClick}
                        onTagClick={handleTagClick}
                    />
                ) : (
                    <EventMapView
                        theme={theme}
                        events={filteredEvents}
                    />
                )}
            </div>

            {/* Screen reader live region — always in DOM so announcements are reliable */}
            <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                {toast}
            </div>

            {/* Toast Message — visual only */}
            {toast && (
                <div aria-hidden="true" className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-200 text-yellow-900 px-6 py-3 rounded shadow-lg text-center font-semibold text-sm animate-fade-in-out">
                    {toast}
                </div>
            )}

            {/* Footer */}
            <div className={`flex-none text-center py-2 text-xs border-t ${theme.border}`}>
                <a
                    href="https://atltech.events"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={theme.link}
                >
                    Powered by ATLTech.Events
                </a>
            </div>
        </div>
    );
}

export default EmbeddedHome;
